import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Form, InputGroup, Row, Col } from '@/components/ui';
import { taskService } from '../../services/quantifiedTaskService';
import { workIdService } from '../../services/workIdService';
import { taskService as taskApi } from '../../services/taskService';
import { routineTaskService } from '../../services/routineTaskService';
import { Effort } from '../../types/Effort';
import { Task, QuantifiedTask, TaskStage } from '../../types/Task';
import type { RoutineTask } from '../../types/RoutineTask';
import TaskModal from './components/taskModals/TaskModal';
import TaskActions from './components/TaskActions';
import TaskFilters from './components/TaskFilters';
import { ForemanArea } from '../../types/ForemanArea';
import CompletionModal from './components/taskModals/CompletionModal';
import { Project } from '../../types/Project';
import { projectService } from '../../services/projectService';
import RequestCorrectionModal from './components/taskModals/RequestCorrectionModal';
import CloseTaskModal from './components/taskModals/CloseTaskModal';
import ReopenTaskModal from './components/taskModals/ReopenTaskModal';
import TaskHistoryModal from '../../components/TaskHistoryModal';
import RoutineTaskModal from '../../components/RoutineTaskModal';
import ResponsiveModal from './components/ResponsiveModal';
import { scrollListToTopAfterPaint } from '../../utils/scrollListToTop';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import ImportModal from './components/taskModals/ImportModal';
import CancelTaskModal from './components/taskModals/CancelTaskModal';
import { TasksTable } from './components/TasksTable';
import ServerPagination from '../../components/ServerPagination';
import { sessionRefresh } from '../../utils/sessionRefresh';
import Toast from './components/Toast';
import SetInternalReviewModal from './components/taskModals/SetInternalReviewModal';
import UnapprovedSubmissionsWarning from './components/UnapprovedSubmissionsWarning';
import { formService, UnapprovedSubmissionCheck } from '../../services/formService';
import { areasService } from '../../services/areasService';
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown';
import AggregationCard from './components/AggregationCard';
import CloneTaskConfirmModal from '../../components/CloneTaskConfirmModal';
import PrintTaskPDFModal from './components/PrintTaskPDFModal';
import './styles/QPTrackingTablet.css';

/**
 * Every dropdown in TaskFilters is a multiselect, so every one of these filters is a LIST and
 * "nothing selected" is an empty list, never an empty string.
 */
interface QPTrackingFilters {
  serviceArea: string[];
  foremanArea: string[];
  contractYear: string[];
  activityNumber: string[];
  projectId: string[];
  status: string[];
  costType: string[];
  plannedStartDateFrom: string;
  plannedStartDateTo: string;
  plannedCompletionDateFrom: string;
  plannedCompletionDateTo: string;
}

/** Seeds both the applied and the pending filter state. Never mutated in place. */
const EMPTY_FILTERS: QPTrackingFilters = {
  serviceArea: [],
  foremanArea: [],
  contractYear: [],
  activityNumber: [],
  projectId: [],
  status: [],
  costType: [],
  plannedStartDateFrom: '',
  plannedStartDateTo: '',
  plannedCompletionDateFrom: '',
  plannedCompletionDateTo: '',
};

const QPTracking: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  // Keeps a list row's effort array in step with a vehicle-fuel PATCH, for rows that HAVE one.
  //
  // The `t.efforts &&` guard is load-bearing now that the list endpoint sends effortSummary instead
  // of effort rows. Without it, `(t.efforts || []).map(...)` would write `efforts: []` onto a row
  // that had no array - turning "no rows loaded, use the summary" into "loaded, and there are
  // none", which zeroes the task's cost and revenue columns. TasksTable separately patches its own
  // lazily-loaded copy, so the open row still updates.
  //
  // Known limitation: the row's aggregate columns (Total Costs, Planned/Actual $) are computed from
  // the server's summary and are NOT recomputed here, so a fuel toggle is reflected in them only
  // after the next list load. It was previously recomputed from the patched rows.
  const mergeVehicleFuelEffortIntoTasks = useCallback((effort: Effort) => {
    if (effort.taskId == null) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id !== effort.taskId || !t.efforts
          ? t
          : {
              ...t,
              efforts: t.efforts.map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
            }
      )
    );
  }, []);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task & Partial<QuantifiedTask> | undefined>(undefined);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filters, setFilters] = useState<QPTrackingFilters>(EMPTY_FILTERS);

  // Separate state for pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState<QPTrackingFilters>(EMPTY_FILTERS);
  
  // Add pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Sort state (default newest first; server allowlist is source of truth - 7.2)
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Stale response guard - only apply response if it matches latest request (7.3)
  const requestIdRef = useRef(0);
  /** Separate guard: totals resolve on their own schedule from the rows. */
  const totalsRequestIdRef = useRef(0);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const pendingScrollAfterCreateRef = useRef(false);
  
  const [isTabletView, setIsTabletView] = useState(false);
  const [isIpad, setIsIpad] = useState(false);
  const [ipadModel, setIpadModel] = useState<'standard' | 'mini' | 'air' | 'pro'>('standard');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [isCompletionModalVisible, setIsCompletionModalVisible] = useState(false);
  const [selectedTaskForCompletion, setSelectedTaskForCompletion] = useState<Task | null>(null);
  // `activities` state removed along with fetchActivities - see the note where that function was.
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<Task | null>(null);
  const [printPdfTask, setPrintPdfTask] = useState<Task | null>(null);
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [isRequestCorrectionModalVisible, setIsRequestCorrectionModalVisible] = useState(false);
  const [isSetInternalReviewModalVisible, setIsSetInternalReviewModalVisible] = useState(false);
  const [isCloseTaskModalVisible, setIsCloseTaskModalVisible] = useState(false);
  const [isReopenTaskModalVisible, setIsReopenTaskModalVisible] = useState(false);
  const [selectedTaskForAction, setSelectedTaskForAction] = useState<Task | null>(null);
  // statusFilter/yearFilter were here. Both setters were never called, so both were permanently
  // undefined: they sat in the list effect's dep array and their reads in the query params were
  // dead branches. yearFilter was also a latent bug - it spread `contractYear: yearFilter` AFTER
  // the multiselect's `contractYear: filters.contractYear`, so had anything ever set it, it would
  // have silently replaced the whole multiselect with a single value. Status now comes only from
  // filters.status, contract year only from filters.contractYear.
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isCancelTaskModalVisible, setIsCancelTaskModalVisible] = useState(false);
  const [selectedTasksForActive, setSelectedTasksForActive] = useState<Task[]>([]);
  const [selectedTasksForCancel, setSelectedTasksForCancel] = useState<Task[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [toastConfig, setToastConfig] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning'; show: boolean }>({
    message: '',
    type: 'info',
    show: false
  });
  /** After clone: open Task modal once the refreshed list contains this task id. */
  const pendingClonedTaskOpenRef = useRef<number | null>(null);
  const [cloneConfirmTasks, setCloneConfirmTasks] = useState<Task[] | null>(null);
  const [showUnapprovedWarning, setShowUnapprovedWarning] = useState(false);
  const [unapprovedSubmissionCount, setUnapprovedSubmissionCount] = useState(0);
  const [unapprovedSubmissions, setUnapprovedSubmissions] = useState<UnapprovedSubmissionCheck['submissions']>([]);
  const [pendingInternalReviewTask, setPendingInternalReviewTask] = useState<Task | null>(null);
  const [pendingAction, setPendingAction] = useState<'complete' | 'set-internal-review' | null>(null);
  const [isConvertToRoutineModalVisible, setIsConvertToRoutineModalVisible] = useState(false);
  const [selectedTaskForConvertToRoutine, setSelectedTaskForConvertToRoutine] = useState<Task | null>(null);
  const [showRoutineTaskModal, setShowRoutineTaskModal] = useState(false);
  const [selectedRoutineTask, setSelectedRoutineTask] = useState<RoutineTask | null>(null);
  /** Totals load independently of the rows, so they need their own loading flag. */
  const [totalsLoading, setTotalsLoading] = useState(true);
  const [aggregationTotals, setAggregationTotals] = useState({
    totalExpectedAccomplishment: 0,
    totalActualAccomplishment: 0,
    totalExpectedRevenue: 0,
    totalActualValue: 0,
    totalCosts: 0
  });

  // Add state for server-side filter options
  const [filterOptions, setFilterOptions] = useState({
    serviceAreas: [] as Array<{ id: number; name: string }>,
    foremanAreas: [] as Array<{ 
      id: number; 
      name: string;
      serviceAreaId: number;
      serviceAreaName: string;
    }>,
    contractYears: [] as string[],
    activities: [] as Array<{ 
      activityName: string; 
      activityNumber: string;
      serviceAreaId: number;
      serviceAreaName: string;
    }>,
    projects: [] as Array<{ 
      id: number; 
      name: string; 
      projectNumber: string;
      serviceAreaId: number;
      serviceAreaName: string;
    }>,
    statuses: [] as string[],
    costTypes: [] as string[]
  });

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-CA');
  };

  const fetchForemanAreas = async () => {
    try {
      // Use the areasService instead of direct fetch
      const data = await areasService.getForemanAreas();
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.warn('QPTracking: Foreman areas response is not an array:', data);
        setForemanAreas([]);
        return;
      }
      
      // Convert the service response to the expected format
      const convertedData = data.map(area => ({
        id: area.id,
        name: area.name,
        code: area.code,
        serviceAreaId: area.serviceArea.id,
        serviceArea: {
          id: area.serviceArea.id,
          name: area.serviceArea.name,
          code: area.serviceArea.code,
          foremanAreas: [], // Empty array to satisfy interface
          activities: [] // Empty array to satisfy interface
        },
        contractAreas: area.contractAreas?.map(ca => ({
          id: ca.id,
          name: ca.name,
          code: ca.code || '',
          foremanAreaId: area.id,
          foremanArea: {
            id: area.id,
            name: area.name,
            code: area.code,
            serviceAreaId: area.serviceArea.id
          }
        })) || []
      }));
      
      setForemanAreas(convertedData as unknown as ForemanArea[]);
    } catch (error) {
      console.error('Error fetching foreman areas:', error);
      setForemanAreas([]); // Ensure it's always set to an empty array on error
    }
  };

  // fetchActivities was here, fetching the full activity list on mount. It was dead weight: its
  // only consumer was the `activities` prop on TaskFilters, which feeds `fallbackActivities` -
  // and that memo returns [] whenever `dynamicOptions.activities` is set, which is always, since
  // the dynamicOptions memo below builds every key unconditionally. So the request was made on
  // every mount and the result was discarded. The dropdown has always been fed from
  // /tasks/quantified/filter-options via dynamicOptions.
  //
  // fetchProjects stays: unlike activities, the `projects` state has a real consumer further down
  // (the RoutineTaskModal lookup), so only its TaskFilters prop was dead.

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data);
    } catch (error) {
      console.error('QPTracking: Error fetching projects:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await taskService.getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('QPTracking: Error fetching filter options:', error);
    }
  };

  /**
   * The filter half of the list query, shared by the rows and the summary totals.
   *
   * Both endpoints must be filtered identically - the totals describe the rows, so any
   * divergence surfaces as a summary card that quietly disagrees with the table.
   */
  const buildFilterParams = () => {
    const taskIdParam = searchParams.get('taskId');
    const taskIdNum = taskIdParam ? parseInt(taskIdParam, 10) : undefined;
    const filterByTaskId = taskIdNum != null && !isNaN(taskIdNum) && taskIdNum > 0;

    return {
      ...(filterByTaskId && { taskId: taskIdNum }),
      ...(filters.status.length > 0 && {
        status: filters.status.map((s: string) => String(s).toUpperCase())
      }),
      ...(filters.serviceArea.length > 0 && { serviceArea: filters.serviceArea }),
      ...(filters.foremanArea.length > 0 && { foremanArea: filters.foremanArea }),
      ...(filters.contractYear.length > 0 && { contractYear: filters.contractYear }),
      ...(filters.activityNumber.length > 0 && { activityNumber: filters.activityNumber }),
      ...(filters.projectId.length > 0 && { projectId: filters.projectId }),
      ...(filters.costType.length > 0 && { costType: filters.costType }),
      ...(filters.plannedStartDateFrom && { plannedStartDateFrom: filters.plannedStartDateFrom }),
      ...(filters.plannedStartDateTo && { plannedStartDateTo: filters.plannedStartDateTo }),
      ...(filters.plannedCompletionDateFrom && { plannedCompletionDateFrom: filters.plannedCompletionDateFrom }),
      ...(filters.plannedCompletionDateTo && { plannedCompletionDateTo: filters.plannedCompletionDateTo }),
      ...(searchTerm && { search: searchTerm })
    };
  };

  /**
   * Loads the summary totals independently of the rows.
   *
   * The cost aggregation behind this resolves every matching task id before summing, so
   * with no filters applied it is much slower than the list itself. Running it alongside
   * rather than ahead of the rows is what lets the table paint immediately; the card shows
   * its own loading state until this lands.
   */
  const fetchTotals = async () => {
    totalsRequestIdRef.current += 1;
    const requestId = totalsRequestIdRef.current;
    try {
      setTotalsLoading(true);
      const totals = await taskService.getQuantifiedTaskTotals(buildFilterParams());

      // Filters may have changed while this was in flight - drop stale results.
      if (requestId !== totalsRequestIdRef.current) return;

      setAggregationTotals({
        ...totals,
        totalActualAccomplishment: totals.totalActualAccomplishment || 0
      });
    } catch (error) {
      if (requestId !== totalsRequestIdRef.current) return;
      console.error('Error fetching quantified task totals:', error);
      setAggregationTotals({
        totalExpectedAccomplishment: 0,
        totalActualAccomplishment: 0,
        totalExpectedRevenue: 0,
        totalActualValue: 0,
        totalCosts: 0
      });
    } finally {
      if (requestId === totalsRequestIdRef.current) {
        setTotalsLoading(false);
      }
    }
  };

  const fetchTasks = async (resetPage: boolean = false) => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    try {
      setLoading(true);
      await sessionRefresh.refreshSession();
      
      const currentPage = resetPage ? 1 : pagination.page;
      
      const taskIdParam = searchParams.get('taskId');
      const taskIdNum = taskIdParam ? parseInt(taskIdParam, 10) : undefined;
      const filterByTaskId = taskIdNum != null && !isNaN(taskIdNum) && taskIdNum > 0;
      
      const queryParams = {
        page: filterByTaskId ? 1 : currentPage,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        ...buildFilterParams(),
      };
      
      const response = await taskService.getQuantifiedTasksPaginated(queryParams);
      
      if (requestId !== requestIdRef.current) return;
      
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Invalid response structure: missing or invalid data array');
      }
      
      if (!response.pagination) {
        throw new Error('Invalid response structure: missing pagination info');
      }
      
      if (response.pagination.limit !== pagination.limit) {
        console.warn('Server returned different limit than requested:', {
          requested: pagination.limit,
          received: response.pagination.limit
        });
      }
      
      setTasks(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNextPage: response.pagination.hasNextPage,
        hasPreviousPage: response.pagination.hasPreviousPage
      });
      
      // Totals are no longer part of this response - see fetchTotals below.

      if (pendingScrollAfterCreateRef.current) {
        pendingScrollAfterCreateRef.current = false;
        scrollListToTopAfterPaint(listScrollRef.current);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error in QPTracking fetchTasks:', error);
      setTasks([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
      setAggregationTotals({
        totalExpectedAccomplishment: 0,
        totalActualAccomplishment: 0,
        totalExpectedRevenue: 0,
        totalActualValue: 0,
        totalCosts: 0
      });
      if (error instanceof Error && error.message.includes('Session expired')) {
        alert('Your session has expired. Please refresh the page and try again.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  /** After creating a task: page 1, newest-first (id desc), scroll when fetch completes. */
  const refreshListAfterNewTask = () => {
    pendingScrollAfterCreateRef.current = true;
    const needsSortReset = sortBy !== 'id' || sortOrder !== 'desc';
    const needsPageReset = pagination.page !== 1;

    if (needsSortReset) {
      setSortBy('id');
      setSortOrder('desc');
    }
    if (needsPageReset) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    if (!needsSortReset && !needsPageReset) {
      void fetchTasks();
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // Validate the new page size
    if (!newPageSize || newPageSize < 1 || newPageSize > 500) {
      console.error('Invalid page size:', newPageSize);
      return;
    }
    
    setPagination(prev => ({ 
      ...prev, 
      limit: newPageSize, 
      page: 1 // Reset to first page when changing page size
    }));
  };

  // One effect owns the list request. There were two - one keyed on pagination/sort, one on
  // filters/search - and both fired on mount, on every taskId change (the param was in both dep
  // arrays) and on Apply-from-page-N, each time issuing the same URL twice. Nothing dedupes
  // identical in-flight requests (no TanStack Query here, despite AGENTS.md), so both reached the
  // wire and the server ran the query twice.
  const taskIdFromUrl = searchParams.get('taskId');

  /**
   * Tracks the filter values from the previous run so a filter change can reset to page 1
   * without needing a second effect. Seeded with the initial values so the mount pass is
   * not treated as a filter change.
   */
  const previousFilterStateRef = useRef({ filters, searchTerm });

  useEffect(() => {
    const previous = previousFilterStateRef.current;
    const filtersChanged =
      previous.filters !== filters || previous.searchTerm !== searchTerm;

    previousFilterStateRef.current = { filters, searchTerm };

    // A filter change while off page 1 has to reset pagination. Reset it and return:
    // changing the page re-runs this effect, which then does the fetch. The old code
    // called fetchTasks(true) here, whose response set the page to 1 and re-triggered
    // the other effect - a second full list request for the same data.
    //
    // handleApplyFilters and handleClearFilters already reset the page themselves, so this
    // is only load-bearing for searchTerm: handleSearch, clearSearch and toggleSearch all
    // change the term without touching pagination.
    if (filtersChanged && pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }

    fetchTasks();
  }, [
    pagination.page,
    pagination.limit,
    sortBy,
    sortOrder,
    taskIdFromUrl,
    filters,
    searchTerm,
  ]);

  /**
   * Totals depend only on the filters, not on paging or sorting, so this deliberately
   * omits pagination/sort from its dependencies. Paging through results re-fetches rows
   * but not the (expensive) aggregate, which is unchanged across pages.
   */
  useEffect(() => {
    fetchTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchTerm, taskIdFromUrl]);

  // Initial data loading
  useEffect(() => {
    // Refresh session when component loads
    const initializeComponent = async () => {
      try {
        
        // First try to refresh the session
        await sessionRefresh.refreshSession();
        
        // Then fetch all the data (fetchTasks will be called by the pagination useEffect)
        const results = await Promise.allSettled([
          fetchForemanAreas(),
          fetchProjects(),
          fetchFilterOptions() // Add filter options fetch
        ]);

        // Log results for each fetch
        results.forEach((result, index) => {
          const fetchNames = ['ForemanAreas', 'Projects', 'FilterOptions'];
          if (result.status === 'rejected') {
            console.error(`QPTracking: Failed to fetch ${fetchNames[index]}:`, result.reason);
          } else {
          }
        });
        
      } catch (error) {
        console.error('QPTracking: Error initializing component:', error);
        // Continue with whatever data we can get
      }
    };
    
    initializeComponent();
  }, []);

  // Apply URL search params (e.g. from KPI widget redirect) to filters on mount
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const serviceAreaParam = searchParams.get('serviceArea');
    const foremanAreaParam = searchParams.get('foremanArea');
    const plannedStartDateFromParam = searchParams.get('plannedStartDateFrom');
    const plannedStartDateToParam = searchParams.get('plannedStartDateTo');
    const plannedCompletionDateFromParam = searchParams.get('plannedCompletionDateFrom');
    const plannedCompletionDateToParam = searchParams.get('plannedCompletionDateTo');
    if (
      statusParam ||
      serviceAreaParam ||
      foremanAreaParam ||
      plannedStartDateFromParam ||
      plannedStartDateToParam ||
      plannedCompletionDateFromParam ||
      plannedCompletionDateToParam
    ) {
      const statusList = statusParam
        ? statusParam.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
        : [];
      const foremanAreaList = foremanAreaParam
        ? foremanAreaParam.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      // serviceArea is a multiselect now, so a deep link may carry several comma-joined names.
      const serviceAreaList = serviceAreaParam
        ? serviceAreaParam.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const urlFilters = {
        serviceArea: serviceAreaList,
        foremanArea: foremanAreaList,
        contractYear: [] as string[],
        activityNumber: [] as string[],
        projectId: [] as string[],
        status: statusList,
        costType: [] as string[],
        plannedStartDateFrom: plannedStartDateFromParam ?? '',
        plannedStartDateTo: plannedStartDateToParam ?? '',
        plannedCompletionDateFrom: plannedCompletionDateFromParam ?? '',
        plannedCompletionDateTo: plannedCompletionDateToParam ?? '',
      };
      setPendingFilters(urlFilters);
      setFilters(urlFilters);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchParams]);

  // Open task modal when taskId is in URL (e.g. from Work ID screen). Depend only on taskId so we don't re-run when other URL/state updates cause a re-render.
  useEffect(() => {
    if (!taskIdFromUrl) return;
    const taskId = parseInt(taskIdFromUrl, 10);
    if (isNaN(taskId)) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    taskService.getTask(taskId).then((task) => {
      if (cancelled) return;
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          setSelectedTask(task as Task & Partial<QuantifiedTask>);
          setIsModalVisible(true);
        }
      }, 0);
    }).catch((err) => {
      if (!cancelled) console.error('Failed to load task for taskId:', taskId, err);
    });
    return () => {
      cancelled = true;
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [taskIdFromUrl]);

  useEffect(() => {
    // Check device size and determine if it's an iPad and its orientation
    const checkDeviceSize = () => {
      // Treat short laptop viewports like tablet layout so filters/actions compact
      // and the table viewport remains usable without manual browser zoom.
      const isTabletDevice =
        (window.innerWidth <= 1200 && window.innerWidth > 480) ||
        window.innerHeight <= 820;
      setIsTabletView(isTabletDevice);

      setOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
      
      // iPad detection based on common iPad dimensions
      // iPad dimensions in pixels (width x height)
      // iPad Mini: 768x1024 (portrait), 1024x768 (landscape)
      // iPad/iPad Air: 768-834 x 1024-1180 (portrait), 1024-1180 x 768-834 (landscape)
      // iPad Pro 11": 834x1194 (portrait), 1194x834 (landscape)
      // iPad Pro 12.9": 1024x1366 (portrait), 1366x1024 (landscape)
      
      // Check if device is any iPad model
      const isIpadDevice = 
        // iPad Mini
        ((window.innerWidth >= 750 && window.innerWidth <= 785) && 
         (window.innerHeight >= 1000 && window.innerHeight <= 1040)) ||
        ((window.innerWidth >= 1000 && window.innerWidth <= 1040) && 
         (window.innerHeight >= 750 && window.innerHeight <= 785)) ||
         
        // Standard iPad and iPad Air
        ((window.innerWidth >= 750 && window.innerWidth <= 850) && 
         (window.innerHeight >= 1000 && window.innerHeight <= 1200)) ||
        ((window.innerWidth >= 1000 && window.innerWidth <= 1200) && 
         (window.innerHeight >= 750 && window.innerHeight <= 850)) ||
         
        // iPad Pro
        ((window.innerWidth >= 820 && window.innerWidth <= 1050) && 
         (window.innerHeight >= 1150 && window.innerHeight <= 1380)) ||
        ((window.innerWidth >= 1150 && window.innerWidth <= 1380) && 
         (window.innerHeight >= 820 && window.innerHeight <= 1050));
      
      setIsIpad(isIpadDevice);
      
      // Determine iPad model
      if (isIpadDevice) {
        // iPad Mini detection
        if ((window.innerWidth >= 750 && window.innerWidth <= 785) || 
            (window.innerHeight >= 750 && window.innerHeight <= 785)) {
          setIpadModel('mini');
        }
        // iPad Air detection (specific dimensions)
        else if ((window.innerWidth >= 810 && window.innerWidth <= 830) || 
                 (window.innerHeight >= 810 && window.innerHeight <= 830) ||
                 (window.innerWidth >= 1170 && window.innerWidth <= 1190) || 
                 (window.innerHeight >= 1170 && window.innerHeight <= 1190)) {
          setIpadModel('air');
        }
        // iPad Pro detection
        else if ((window.innerWidth >= 1000 && window.innerWidth <= 1050) || 
                 (window.innerHeight >= 1000 && window.innerHeight <= 1050) ||
                 (window.innerWidth >= 1350 && window.innerWidth <= 1380) || 
                 (window.innerHeight >= 1350 && window.innerHeight <= 1380)) {
          setIpadModel('pro');
        }
        // Standard iPad
        else {
          setIpadModel('standard');
        }
      }
    };

    // Initial check
    checkDeviceSize();

    // Add event listeners for window resize and orientation change
    window.addEventListener('resize', checkDeviceSize);
    window.addEventListener('orientationchange', checkDeviceSize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkDeviceSize);
      window.removeEventListener('orientationchange', checkDeviceSize);
    };
  }, []);

  const handleAddTask = () => {
    setSelectedTask(undefined);
    setIsModalVisible(true);
  };

  const handleReviewTask = async (task: Task) => {
    try {
      // Fetch the full task data to ensure all fields (including Work Planning fields) are loaded
      const fullTask = await taskService.getTask(task.id);
      setSelectedTask(fullTask as Task & Partial<QuantifiedTask>);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching task for review:', error);
      // Fallback to using the task from the list if fetch fails
    setSelectedTask(task as Task & Partial<QuantifiedTask>);
    setIsModalVisible(true);
    }
  };

  useEffect(() => {
    const pendingId = pendingClonedTaskOpenRef.current;
    if (pendingId == null || loading) return;
    const match = tasks.find((t) => t.id === pendingId);
    if (!match) return;
    pendingClonedTaskOpenRef.current = null;
    void (async () => {
      try {
        const fullTask = await taskService.getTask(match.id);
        setSelectedTask(fullTask as Task & Partial<QuantifiedTask>);
        setIsModalVisible(true);
      } catch {
        setSelectedTask(match as Task & Partial<QuantifiedTask>);
        setIsModalVisible(true);
      }
    })();
  }, [tasks, loading]);

  const handleReviewEffort = (task: Task) => {
  };


  const handleMarkComplete = async (task: Task) => {
    setSelectedTaskForCompletion(task);
    setIsCompletionModalVisible(true);
  };

  const handleMarkForReview = async (task: Task) => {
  };

  // Memoised because TaskFilters puts this in the dep array of its three option-pruning effects.
  // Recreated every render, it made those effects re-run on every render - harmless today since
  // they early-return, but wasted work on a hot path and a loop waiting to happen if anyone adds
  // a fetch to one of them. The updater form means it needs no dependencies.
  const handleFilterChange = useCallback((name: string, value: string | string[]) => {
    setPendingFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    // Reset to page 1 when applying filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setPendingFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    // Reset to page 1 when clearing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = () => {
    return filters.serviceArea.length > 0 ||
           filters.foremanArea.length > 0 ||
           filters.contractYear.length > 0 ||
           filters.activityNumber.length > 0 ||
           filters.projectId.length > 0 ||
           filters.status.length > 0 ||
           filters.costType.length > 0 ||
           filters.plannedStartDateFrom !== '' ||
           filters.plannedStartDateTo !== '' ||
           filters.plannedCompletionDateFrom !== '' ||
           filters.plannedCompletionDateTo !== '';
  };

  // Check if pending filters are different from applied filters
  const hasUnappliedChanges = () => {
    const compareArrays = (a: string[], b: string[]): boolean => {
      return a.length !== b.length || !a.every((v, i) => v === b[i]);
    };
    
    return compareArrays(pendingFilters.serviceArea, filters.serviceArea) ||
           compareArrays(pendingFilters.foremanArea, filters.foremanArea) ||
           compareArrays(pendingFilters.contractYear, filters.contractYear) ||
           compareArrays(pendingFilters.activityNumber, filters.activityNumber) ||
           compareArrays(pendingFilters.projectId, filters.projectId) ||
           compareArrays(pendingFilters.status, filters.status) ||
           compareArrays(pendingFilters.costType, filters.costType) ||
           pendingFilters.plannedStartDateFrom !== filters.plannedStartDateFrom ||
           pendingFilters.plannedStartDateTo !== filters.plannedStartDateTo ||
           pendingFilters.plannedCompletionDateFrom !== filters.plannedCompletionDateFrom ||
           pendingFilters.plannedCompletionDateTo !== filters.plannedCompletionDateTo;
  };

  const handleTaskComplete = async () => {
    try {
      await fetchTasks();
    } catch (error) {
      console.error('Error handling task completion:', error);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToastConfig({ message, type, show: true });
  };

  // Function to check for unapproved submissions for a task
  const checkUnapprovedSubmissions = async (taskId: number): Promise<UnapprovedSubmissionCheck> => {
    try {
      return await formService.checkUnapprovedSubmissions(taskId);
    } catch (error) {
      console.error('Error checking unapproved submissions:', error);
      return { count: 0, submissions: [] };
    }
  };

  const handleTaskSaved = async (savedTask: Task, options?: { isNew?: boolean }) => {
    try {
    setIsModalVisible(false);
    setSelectedTask(undefined);

      const isNew = options?.isNew === true;
      if (isNew) {
        refreshListAfterNewTask();
      } else {
        await fetchTasks();
      }

      showToast('Task saved successfully', 'success');
    } catch (error) {
      console.error('Error refreshing tasks after save:', error);
      showToast('Task saved but failed to refresh list', 'warning');
    }
  };

  const handleDuplicateTasks = async (
    tasksToUpdate: Task[],
    names?: string[],
    quantifiedOptions?: { contractYear?: string; status?: string }
  ) => {
    try {
      setLoading(true);
      const cloned = await taskService.duplicateTasks(
        tasksToUpdate,
        names,
        quantifiedOptions
      );
      setSelectedTasks([]);

      const primary = cloned[0];
      const needle = primary
        ? (primary.taskNumber || String(primary.id)).trim()
        : '';

      if (needle) {
        setIsSearchVisible(true);
        setSearchInput(needle);
        setSearchTerm(needle);
      }

      if (cloned.length === 1 && tasksToUpdate.length === 1) {
        pendingClonedTaskOpenRef.current = cloned[0].id;
        const from = tasksToUpdate[0].taskNumber || `#${tasksToUpdate[0].id}`;
        const created = cloned[0].taskNumber || `#${cloned[0].id}`;
        showToast(
          `New task ${created} — cloned from ${from}. Search updated; opening the new task.`,
          'success'
        );
      } else {
        pendingClonedTaskOpenRef.current = null;
        const createdList = cloned
          .map((t) => t.taskNumber || `#${t.id}`)
          .join(', ');
        showToast(
          `${cloned.length} new task(s): ${createdList}. Search set to ${needle || 'first new task'}.`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error duplicating tasks:', error);
      pendingClonedTaskOpenRef.current = null;
      showToast(
        error instanceof Error ? error.message : 'Failed to clone tasks',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTaskAction = async (action: string, task: Task, selectedTasksList?: Task[]) => {
    // Handle bulk actions from table selection or checkboxes
    if (selectedTasksList?.length && selectedTasksList.length > 0 || selectedTasks.length > 0) {
      const tasksToUpdate = selectedTasksList || filteredTasks.filter(t => selectedTasks.includes(t.id));
      switch (action) {
        case 'set-active':
          handleSetActive(tasksToUpdate, 'Task set to active');
          return;
        case 'cancel':
          setSelectedTasksForCancel(tasksToUpdate);
          setIsCancelTaskModalVisible(true);
          return;
        case 'duplicate':
          setCloneConfirmTasks(tasksToUpdate);
          return;
      }
    }

    // Handle single task actions (context menu)
    switch (action) {
      case 'set-active':
        handleSetActive([task], 'Task set to active');
        break;
      case 'cancel':
        setSelectedTasksForCancel([task]);
        setIsCancelTaskModalVisible(true);
        break;
      case 'request-correction':
        setIsRequestCorrectionModalVisible(true);
        setSelectedTaskForAction(task);
        break;
      case 'close':
        setIsCloseTaskModalVisible(true);
        setSelectedTaskForAction(task);
        break;
      case 'reopen':
        setIsReopenTaskModalVisible(true);
        setSelectedTaskForAction(task);
        break;
      case 'review':
        handleReviewTask(task);
        break;
      case 'print-pdf':
        setPrintPdfTask(task);
        break;
      case 'effort':
        handleReviewEffort(task);
        break;
      case 'history':
        await handleViewHistory(task);
        break;
      case 'remove-work-id':
        if (task.workIdId != null) {
          try {
            await workIdService.unassign(task.workIdId, task.id);
            await fetchTasks();
            setToastConfig({ message: 'Work ID removed from task', type: 'success', show: true });
          } catch (err) {
            console.error('Error removing Work ID from task:', err);
            setToastConfig({ message: err instanceof Error ? err.message : 'Failed to remove Work ID from task', type: 'error', show: true });
          }
        }
        break;
      case 'complete':
        // Check for unapproved submissions before proceeding with completion
        try {
          const submissionCheck = await checkUnapprovedSubmissions(task.id);
          if (submissionCheck.count > 0) {
            // Show warning dialog
            setUnapprovedSubmissionCount(submissionCheck.count);
            setUnapprovedSubmissions(submissionCheck.submissions);
            setPendingInternalReviewTask(task);
            setPendingAction('complete');
            setShowUnapprovedWarning(true);
          } else {
            // No unapproved submissions, proceed directly with completion
            setSelectedTaskForCompletion(task);
            setIsCompletionModalVisible(true);
          }
        } catch (error) {
          console.error('Error checking submissions:', error);
          // If check fails, proceed anyway
          setSelectedTaskForCompletion(task);
          setIsCompletionModalVisible(true);
        }
        break;
      case 'duplicate':
        setCloneConfirmTasks([task]);
        break;
      case 'clone-task':
        setCloneConfirmTasks([task]);
        break;
      case 'convert-to-routine':
        setSelectedTaskForConvertToRoutine(task);
        setIsConvertToRoutineModalVisible(true);
        break;
      case 'set-internal-review':
        // Check for unapproved submissions before proceeding
        try {
          const submissionCheck = await checkUnapprovedSubmissions(task.id);
          if (submissionCheck.count > 0) {
            // Show warning dialog
            setUnapprovedSubmissionCount(submissionCheck.count);
            setUnapprovedSubmissions(submissionCheck.submissions);
            setPendingInternalReviewTask(task);
            setPendingAction('set-internal-review');
            setShowUnapprovedWarning(true);
          } else {
            // No unapproved submissions, proceed directly
            setIsSetInternalReviewModalVisible(true);
            setSelectedTaskForAction(task);
          }
        } catch (error) {
          console.error('Error checking submissions:', error);
          // If check fails, proceed anyway
          setIsSetInternalReviewModalVisible(true);
          setSelectedTaskForAction(task);
        }
        break;
      default:
    }
  };

  const handleSetActive = async (tasks: Task[], successMessage: string) => {
    try {
      if (!currentUser?.id) throw new Error('User not authenticated');
      setLoading(true);

      if (tasks.length > 1) {
        await taskService.bulkUpdateStatus({
          taskIds: tasks.map(t => t.id),
          status: TaskStage.ACTIVE,
          comments: successMessage,
          userId: currentUser.id
        });
        showToast(`${tasks.length} tasks set to active`, 'success');
      } else if (tasks.length === 1) {
        await taskService.updateTaskStatus(tasks[0].id, {
          status: TaskStage.ACTIVE,
          comments: successMessage,
          userId: currentUser.id,
          taskId: tasks[0].id
        });
        showToast('Task set to active', 'success');
      }

      await fetchTasks();
      setSelectedTasks([]);
    } catch (error) {
      console.error('Error setting tasks to active:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to set tasks to active',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCorrection = async () => {
    try {
      await fetchTasks(); // Refresh after correction request
    } catch (error) {
      console.error('Error requesting correction:', error);
    }
  };

  const handleCloseTask = async (taskId: number, comments: string | null) => {
    try {
      setLoading(true);
      
      // Close the task
      await taskService.closeTask(taskId, {
        comments: comments || 'Task closed',
        userId: currentUser?.id,
        taskId: taskId
      });
      
      await fetchTasks();
      setIsCloseTaskModalVisible(false);
      setSelectedTaskForAction(null);
    } catch (error) {
      console.error('Error closing task:', error);
      alert('Failed to close task');
    } finally {
      setLoading(false);
    }
  };

  const handleReopenTask = async (comments: string) => {
    if (!selectedTaskForAction) return;
    try {
      await taskService.reopenTask(selectedTaskForAction.id, {
        comments,
        userId: currentUser?.id,
        taskId: selectedTaskForAction.id
      });
      await fetchTasks();
      showToast('Task reopened successfully', 'success');
    } catch (error) {
      console.error('Error reopening task:', error);
      showToast('Failed to reopen task', 'error');
    }
  };

  const handleSetInternalReview = async (comments: string) => {
    if (!selectedTaskForAction) return;
    try {
      await taskService.setToInternalReview(selectedTaskForAction.id, {
        comments,
        userId: currentUser?.id
      });
      await fetchTasks();
      showToast('Task set to internal review successfully', 'success');
    } catch (error) {
      console.error('Error setting task to internal review:', error);
      showToast('Failed to set task to internal review', 'error');
      throw error; // Re-throw error so modal can handle it
    }
  };

  const handleConfirmConvertToRoutine = async () => {
    const task = selectedTaskForConvertToRoutine;
    if (!task) return;
    try {
      const updated = await taskApi.convertTaskToRoutine(task.id);
      await fetchTasks();
      setIsConvertToRoutineModalVisible(false);
      setSelectedTaskForConvertToRoutine(null);
      let routineTask: RoutineTask;
      try {
        routineTask = await routineTaskService.getTaskDetails(updated.id);
      } catch (e) {
        // Build routine task shape from convert response if getTaskDetails fails
        const rt = (updated as any).routineTasks?.[0];
        routineTask = {
          ...updated,
          taskId: updated.id,
          id: updated.id,
          labourAllocation: rt?.labourAllocation ?? 100,
          materialAllocation: rt?.materialAllocation ?? 100,
          equipmentAllocation: rt?.equipmentAllocation ?? 100,
          secondaryLabourAllocation: rt?.secondaryLabourAllocation ?? null,
          secondaryMaterialAllocation: rt?.secondaryMaterialAllocation ?? null,
          secondaryEquipmentAllocation: rt?.secondaryEquipmentAllocation ?? null,
          taskSubType: rt?.taskSubType ?? null,
          clonedTaskId: rt?.clonedTaskId ?? null
        } as RoutineTask;
      }
      setSelectedRoutineTask(routineTask);
      setShowRoutineTaskModal(true);
      showToast('Task converted to Routine. Complete routine details and save.', 'success');
    } catch (error: any) {
      console.error('Error converting task to routine:', error);
      showToast(error?.response?.data?.error || 'Failed to convert task to routine', 'error');
    }
  };

  // Handle warning dialog actions
  const handleUnapprovedWarningConfirm = () => {
    setShowUnapprovedWarning(false);
    if (pendingInternalReviewTask && pendingAction) {
      if (pendingAction === 'set-internal-review') {
        setIsSetInternalReviewModalVisible(true);
        setSelectedTaskForAction(pendingInternalReviewTask);
      } else if (pendingAction === 'complete') {
        setSelectedTaskForCompletion(pendingInternalReviewTask);
        setIsCompletionModalVisible(true);
      }
      setPendingInternalReviewTask(null);
      setPendingAction(null);
      setUnapprovedSubmissions([]);
    }
  };

  const handleUnapprovedWarningCancel = () => {
    setShowUnapprovedWarning(false);
    setPendingInternalReviewTask(null);
    setPendingAction(null);
    setUnapprovedSubmissionCount(0);
    setUnapprovedSubmissions([]);
  };

  /**
   * Opens the history modal with the history fetched on demand.
   *
   * The list payload deliberately no longer carries `taskHistory` (see `buildTaskListSelect`): it
   * was the largest child relation on a page of up to 500 rows, one row per status transition plus
   * a joined user each, and this modal was its only reader on this screen. So the row is enriched
   * with a fresh `getTask` first - the same thing `handleReviewTask` does for `TaskModal` - and
   * only `taskHistory` is merged in, leaving the list row's own shape untouched.
   */
  const handleViewHistory = async (task: Task) => {
    try {
      const fullTask = await taskService.getTask(task.id);
      setSelectedTaskForHistory({ ...task, taskHistory: fullTask.taskHistory || [] });
    } catch (error) {
      console.error('Error fetching task history:', error);
      // Open anyway: the modal renders "No history records found." and can still take a comment.
      setSelectedTaskForHistory({ ...task, taskHistory: [] });
    }
    setIsHistoryModalVisible(true);
  };

  const handleAddHistoryComment = async (updatedTask: Task) => {
    // Update the task in the local state
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === updatedTask.id ? updatedTask : task
      )
    );
  };

  const handleCancelTask = async (tasks: Task[], comments: string) => {
    try {
      if (!currentUser?.id) throw new Error('User not authenticated');
      setLoading(true);

      // Use different methods for bulk vs single
      if (tasks.length > 1) {
        await taskService.bulkUpdateStatus({
          taskIds: tasks.map(t => t.id),
          status: TaskStage.CANCELLED,
          comments,
          userId: currentUser.id
        });
      } else if (tasks.length === 1) {
        await taskService.cancelTask(tasks[0].id, {
          comments,
          userId: currentUser.id,
          taskId: tasks[0].id
        });
      }

      await fetchTasks();
      setIsCancelTaskModalVisible(false);
      setSelectedTasksForCancel([]);
      setSelectedTasks([]);
    } catch (error: unknown) {
      console.error('Error cancelling tasks:', error);
      const msg =
        typeof error === 'object' && error != null && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      alert(msg || (error instanceof Error ? error.message : 'Failed to cancel tasks'));
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTasks = () => {
    // Since we're using server-side pagination and filtering,
    // we just return the tasks as they come from the server
    return tasks;
  };

  const filteredTasks = getFilteredTasks();

  // Use server-side filter options with bidirectional cross-filtering logic
  // Use pendingFilters for dynamic filtering to provide immediate visual feedback
  const dynamicOptions = useMemo(() => {
    const activityNumberMatches = (
      selectedNumbers: string[],
      activityNumber: string | number | null | undefined
    ): boolean =>
      selectedNumbers.some((n) => String(n) === String(activityNumber ?? ''));

    /**
     * Service Area and Project are multiselects, so the cascades below match against a SET of
     * selections. An EMPTY set means "no constraint" and must leave the list untouched - testing
     * membership of an empty set is false for every option and would hide all of them. A non-empty
     * set is a union (keep an option belonging to ANY selected value), matching the server's OR.
     */
    const selectedServiceAreaSet = new Set(pendingFilters.serviceArea);
    const hasServiceAreaFilter = selectedServiceAreaSet.size > 0;
    const inSelectedServiceAreas = (serviceAreaName: string | undefined | null): boolean =>
      !hasServiceAreaFilter || (!!serviceAreaName && selectedServiceAreaSet.has(serviceAreaName));

    const selectedProjectSet = new Set(pendingFilters.projectId.map((id) => String(id)));
    const hasProjectFilter = selectedProjectSet.size > 0;
    /** Service-area names of the selected projects; [] when nothing is selected. */
    const selectedProjectServiceAreaNames = hasProjectFilter
      ? filterOptions.projects
          .filter((project: any) => selectedProjectSet.has(String(project.id)))
          .map((project: any) => project.serviceAreaName)
          .filter((name: any): name is string => !!name)
      : [];

    // Helper function to find service areas based on current selections
    const getFilteredServiceAreas = () => {
      let filteredServiceAreas = filterOptions.serviceAreas;
      
      // If foreman area is selected, only show the service area that contains it
      if (pendingFilters.foremanArea.length > 0) {
        const selectedForemanAreas = filterOptions.foremanAreas.filter((area: any) => 
          pendingFilters.foremanArea.includes(area.name)
        );
        if (selectedForemanAreas.length > 0) {
          const serviceAreaNames = selectedForemanAreas.map((area: any) => area.serviceAreaName);
          filteredServiceAreas = filterOptions.serviceAreas.filter((area: any) => 
            serviceAreaNames.includes(area.name)
          );
        }
      }
      
      // If activity is selected, find all service areas that contain this activity
      if (pendingFilters.activityNumber.length > 0 && pendingFilters.foremanArea.length === 0) {
        const matchingActivities = filterOptions.activities.filter((activity: any) =>
          activityNumberMatches(pendingFilters.activityNumber, activity.activityNumber)
        );
        if (matchingActivities.length > 0) {
          const serviceAreaNames = matchingActivities.map((activity: any) => activity.serviceAreaName);
          filteredServiceAreas = filterOptions.serviceAreas.filter((area: any) => 
            serviceAreaNames.includes(area.name)
          );
        }
      }
      
      // If project(s) are selected, only show the service areas that contain them
      if (hasProjectFilter && pendingFilters.foremanArea.length === 0 && pendingFilters.activityNumber.length === 0) {
        if (selectedProjectServiceAreaNames.length > 0) {
          filteredServiceAreas = filterOptions.serviceAreas.filter((area: any) =>
            selectedProjectServiceAreaNames.includes(area.name)
          );
        }
      }

      // Copy before sorting: when no cascade narrowed the list, filteredServiceAreas is still the
      // filterOptions state array itself, and Array.sort mutates in place.
      return [...filteredServiceAreas].sort((a: any, b: any) => a.name.localeCompare(b.name));
    };

    // Helper function to find foreman areas based on current selections
    const getFilteredForemanAreas = () => {
      let filteredForemanAreas = filterOptions.foremanAreas;

      // If service area(s) are selected, filter foreman areas to their union
      if (hasServiceAreaFilter) {
        filteredForemanAreas = filterOptions.foremanAreas.filter((area: any) =>
          inSelectedServiceAreas(area.serviceAreaName)
        );
      }

      // If activity is selected, filter foreman areas to those in service areas that contain this activity
      if (pendingFilters.activityNumber.length > 0) {
        const matchingActivities = filterOptions.activities.filter((activity: any) =>
          activityNumberMatches(pendingFilters.activityNumber, activity.activityNumber)
        );
        if (matchingActivities.length > 0) {
          const serviceAreaNames = matchingActivities.map((activity: any) => activity.serviceAreaName);
          filteredForemanAreas = filteredForemanAreas.filter((area: any) => 
            serviceAreaNames.includes(area.serviceAreaName)
          );
        }
      }
      
      // If project(s) are selected, filter foreman areas to those in the same service areas
      if (hasProjectFilter && selectedProjectServiceAreaNames.length > 0) {
        filteredForemanAreas = filteredForemanAreas.filter((area: any) =>
          selectedProjectServiceAreaNames.includes(area.serviceAreaName)
        );
      }

      // Copy before sorting - see the note in getFilteredServiceAreas.
      return [...filteredForemanAreas].sort((a: any, b: any) => a.name.localeCompare(b.name));
    };

    // Helper function to find activities based on current selections
    const getFilteredActivities = () => {
      let filteredActivities = filterOptions.activities;

      // If service area(s) are selected, filter activities to their union
      if (hasServiceAreaFilter) {
        filteredActivities = filterOptions.activities.filter((activity: any) =>
          inSelectedServiceAreas(activity.serviceAreaName)
        );
      }

      // If foreman area(s) are selected, filter activities to those in the same service areas
      if (pendingFilters.foremanArea.length > 0) {
        const selectedForemanAreas = filterOptions.foremanAreas.filter((area: any) =>
          pendingFilters.foremanArea.includes(area.name)
        );
        if (selectedForemanAreas.length > 0) {
          filteredActivities = filteredActivities.filter((activity: any) => 
            selectedForemanAreas.some((area: any) => area.serviceAreaName === activity.serviceAreaName)
          );
        }
      }
      
      // Deduplicate activities by activityNumber + activityName
      // Keep the first occurrence of each unique activity
      const activityMap = new Map();
      filteredActivities.forEach((activity: any) => {
        const key = `${activity.activityNumber}|${activity.activityName}`;
        if (!activityMap.has(key)) {
          // Store the activity without service area context for the dropdown
          activityMap.set(key, {
            activityName: activity.activityName,
            activityNumber: activity.activityNumber
          });
        }
      });
      
      return Array.from(activityMap.values()).sort((a: any, b: any) => {
        // Sort by activity number first, then by activity name
        const numberA = parseInt(a.activityNumber) || 0;
        const numberB = parseInt(b.activityNumber) || 0;
        
        if (numberA !== numberB) {
          return numberA - numberB;
        }
        
        return a.activityName.localeCompare(b.activityName);
      });
    };

    // Helper function to find projects based on current selections
    const getFilteredProjects = () => {
      let filteredProjects = filterOptions.projects;

      // If service area(s) are selected, filter projects to their union
      if (hasServiceAreaFilter) {
        filteredProjects = filterOptions.projects.filter((project: any) =>
          inSelectedServiceAreas(project.serviceAreaName)
        );
      }

      // If foreman area(s) are selected, filter projects to those in the same service areas
      if (pendingFilters.foremanArea.length > 0) {
        const selectedForemanAreas = filterOptions.foremanAreas.filter((area: any) =>
          pendingFilters.foremanArea.includes(area.name)
        );
        if (selectedForemanAreas.length > 0) {
          filteredProjects = filteredProjects.filter((project: any) => 
            selectedForemanAreas.some((area: any) => area.serviceAreaName === project.serviceAreaName)
          );
        }
      }
      
      // If activity is selected, filter projects to those in service areas that contain this activity
      if (pendingFilters.activityNumber.length > 0) {
        const matchingActivities = filterOptions.activities.filter((activity: any) =>
          activityNumberMatches(pendingFilters.activityNumber, activity.activityNumber)
        );
        if (matchingActivities.length > 0) {
          const serviceAreaNames = matchingActivities.map((activity: any) => activity.serviceAreaName);
          filteredProjects = filteredProjects.filter((project: any) => 
            serviceAreaNames.includes(project.serviceAreaName)
          );
        }
      }
      
      // Copy before sorting - see the note in getFilteredServiceAreas.
      return [...filteredProjects].sort((a: any, b: any) => a.name.localeCompare(b.name));
    };

    const result = {
      serviceArea: getFilteredServiceAreas(),
      foremanArea: getFilteredForemanAreas(),
      contractYear: [...filterOptions.contractYears].sort((a: any, b: any) => a.localeCompare(b)),
      activities: getFilteredActivities(),
      projects: getFilteredProjects(),
      status: filterOptions.statuses.map(status => status),
      costTypes: [...filterOptions.costTypes].sort((a: any, b: any) => a.localeCompare(b)),
    };
    
    return result;
  }, [filterOptions, pendingFilters.serviceArea, pendingFilters.foremanArea, pendingFilters.contractYear, pendingFilters.activityNumber, pendingFilters.projectId]);

  const handleTaskSelection = (taskIds: number[]) => {
    setSelectedTasks(taskIds);
  };

  const handleImportTasks = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use the upload endpoint that processes validated tasks
      const response = await fetch('/api/tasks/quantified/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
          // Don't set Content-Type when using FormData
        },
        // This prevents the default form submission behavior
        credentials: 'same-origin'
      });
      
      // Handle non-OK responses
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed with status:', response.status, errorText);
        
        // Try to parse the error response as JSON
        let errorMessage = 'Failed to upload tasks';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If it's not valid JSON, use the raw text or status text
          errorMessage = errorText || response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      // Parse and return the successful response
      const result = await response.json();
      
      // Only refresh tasks if there were successful uploads
      if (result.successCount > 0) {
        await fetchTasks();
      }
      
      return result;
    } catch (error) {
      console.error('Error uploading tasks:', error);
      throw error;
    }
  };

  const handleImportClick = () => {
    setIsImportModalVisible(true);
  };

  const handleBulkAction = async (action: string, selectedTasks: Task[]) => {
    try {
      switch (action) {
        case 'duplicate':
          setCloneConfirmTasks(selectedTasks);
          break;
        
        case 'cancel':
          setSelectedTasksForCancel(selectedTasks);
          setIsCancelTaskModalVisible(true);
          break;
        
        case 'set-active':
          setSelectedTasksForActive(selectedTasks);
          break;

        default:
          console.warn('Unknown bulk action:', action);
      }
    } catch (error) {
      console.error('Error in bulk action:', error);
      alert('Failed to perform bulk action');
    }
  };

  // Helper function to get the correct top margin for different iPad models
  const getIpadTopMargin = (model: 'standard' | 'mini' | 'air' | 'pro', orientation: 'portrait' | 'landscape'): string => {
    if (orientation === 'landscape') {
      switch (model) {
        case 'mini':
          return '40px';
        case 'standard':
          return '45px';
        case 'air':
          return '45px';
        case 'pro':
          return '50px';
        default:
          return '45px';
      }
    } else { // portrait
      switch (model) {
        case 'mini':
          return '45px';
        case 'standard':
          return '50px';
        case 'air':
          return '55px';
        case 'pro':
          return '60px';
        default:
          return '50px';
      }
    }
  };

  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    if (isSearchVisible) {
      setSearchTerm(''); // Clear search when hiding
      setSearchInput(''); // Clear input when hiding
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  // There used to be an `if (loading) return <Spinner/>` early return here, replacing the entire
  // page on every list fetch. It unmounted TaskFilters, TasksTable, AggregationCard, TaskModal and
  // RoutineTaskModal and then remounted them when the response landed, which had three costs:
  //
  //  - every mount-time effect in that subtree re-ran per list request. TaskModal's activities and
  //    projects effects were unguarded, so each filter Apply refetched both.
  //  - the filter card collapsed and any open multiselect closed on every Apply, and
  //    TaskFilters' local showAdditionalFilters state reset.
  //  - the table lost scroll position and expanded rows.
  //
  // TasksTable already renders its own translucent overlay from the `loading` prop it is given
  // below, so the spinner is not lost - it just no longer costs a remount.

  const taskFiltersSection = (
    <TaskFilters
      tasks={tasks}
      foremanAreas={foremanAreas}
      projects={projects}
      filters={pendingFilters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
      hasActiveFilters={hasActiveFilters()}
      showApplyButton={true}
      onApply={handleApplyFilters}
      applyDisabled={!hasUnappliedChanges()}
      searchTerm={searchInput}
      onSearchChange={handleSearchInputChange}
      isSearchVisible={isSearchVisible}
      onToggleSearch={toggleSearch}
      dynamicOptions={dynamicOptions}
      isTabletLayout={isTabletView}
      defaultCollapsed={isTabletView}
    />
  );

  const aggregationSection = (
    <AggregationCard
      totals={aggregationTotals}
      loading={totalsLoading}
      filters={filters}
    />
  );

  const searchSection = isSearchVisible ? (
    <Row className="tw-mb-3 search-container tw-shrink-0">
      <Col xs={12}>
        <InputGroup className="tw-shadow-sm">
          <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            size="sm"
            placeholder="Search by Task Name, Task Number, Asset, Description, Project, or Activity..."
            value={searchInput}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            autoFocus
          />
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={handleSearch}
            title="Search"
          >
            <i className="bi bi-search"></i>
          </Button>
          {(searchInput || searchTerm) && (
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={clearSearch}
              title="Clear search"
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          )}
        </InputGroup>
        {searchTerm && (
          <div className="tw-text-content-muted tw-text-sm tw-mt-1">
            Searching for: "{searchTerm}"
          </div>
        )}
      </Col>
    </Row>
  ) : null;

  const tableBlock = (
    <div
      ref={listScrollRef}
      className={`tw-grow table-scroll-wrapper ${isIpad && orientation === 'portrait' ? 'tw-mt-4' : 'tw-mt-3'}`}
      style={{ minHeight: 0, minWidth: 0 }}
    >
      {/* No extra overflow wrapper here: TasksTable owns the single horizontal scroller
          (its inner scroll region + synced sticky top-scrollbar). A redundant
          overflow-x-auto wrapper here created nested/ambiguous scrollbars on desktop. */}
      <TasksTable
        tasks={filteredTasks}
        onTaskAction={handleTaskAction}
        onTaskClick={handleReviewTask}
        onTaskSelect={handleTaskSelection}
        onBulkAction={handleBulkAction}
        selectedTasks={selectedTasks}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        loading={loading}
        onVehicleFuelEffortPatched={mergeVehicleFuelEffortIntoTasks}
      />
    </div>
  );

  const paginationBlock = (
    <div className="tw-shrink-0">
      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-3 tw-mb-4"
      />
      {isTabletView && <div style={{ height: '60px' }}></div>}
    </div>
  );

  return (
    <div className="qp-tracking-page tw-w-full tw-flex tw-flex-col tw-h-full tw-overflow-hidden" style={{ padding: '20px' }}>
      <Toast
        message={toastConfig.message}
        type={toastConfig.type}
        show={toastConfig.show}
        onClose={() => setToastConfig(prev => ({ ...prev, show: false }))}
              />
        
        <div
          className={`tw-flex tw-justify-between tw-items-center tw-shrink-0 tw-gap-2 ${
            isTabletView ? 'tw-mb-2 tw-flex-wrap' : 'tw-mb-4'
          }`}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <h1 className={`tw-mb-0 ${isTabletView ? 'tw-text-2xl' : ''}`}>Quantified Tasks</h1>
          </div>
          <div className={`tw-flex tw-items-center ${isTabletView ? 'tw-gap-1' : 'tw-gap-2'}`}>
                <Button
                  // Refresh is chrome, not a success outcome — was `variant="success"` (green).
                  variant="outline-secondary"
                  size={isTabletView ? 'sm' : undefined}
                  onClick={() => {
                    setLoading(true);
                    Promise.all([
                      fetchTasks(),
                      fetchForemanAreas(),
                      fetchProjects(),
                      fetchFilterOptions()
                    ]).finally(() => setLoading(false));
                  }}
                  title="Refresh"
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  {!isTabletView && <span className="tw-ml-2">Refresh</span>}
                  {isTabletView && <span className="tw-sr-only">Refresh</span>}
                </Button>
                <Button
                  variant={isSearchVisible ? 'primary' : 'outline-primary'}
                  active={!!searchTerm}
                  size={isTabletView ? 'sm' : undefined}
                  className={!isSearchVisible ? 'search-btn' : ''}
                  onClick={toggleSearch}
                  title={
                    isSearchVisible
                      ? 'Close search'
                      : searchTerm
                        ? `Search tasks (active: ${searchTerm})`
                        : 'Search tasks'
                  }
                >
                  <i className={`bi ${isSearchVisible ? 'bi-x-lg' : 'bi-search'} search-btn-icon${!isTabletView ? ' tw-mr-2' : ''}`}></i>
                  {!isTabletView && (
                    <span>
                      Search {searchTerm ? `(${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm})` : ''}
                    </span>
                  )}
                  {isTabletView && <span className="tw-sr-only">Search</span>}
                </Button>
                {hasPermission('quantified:task_create') && (
                  <Button
                    variant="primary"
                    size={isTabletView ? 'sm' : undefined}
                    onClick={handleAddTask}
                    title="Add Task"
                  >
                    <i className="bi bi-plus-lg"></i>
                    {!isTabletView && <span className="tw-ml-2">Add Task</span>}
                    {isTabletView && <span className="tw-sr-only">Add Task</span>}
                  </Button>
                )}
                <Button
                  variant="outline-secondary"
                  size={isTabletView ? 'sm' : undefined}
                  onClick={handleImportClick}
                  title="Import"
                >
                  <i className="bi bi-upload"></i>
                  {!isTabletView && <span className="tw-ml-2">Import</span>}
                  {isTabletView && <span className="tw-sr-only">Import</span>}
                </Button>
              </div>
            </div>

            <div className="tw-shrink-0">{taskFiltersSection}</div>
            <div className="tw-shrink-0">{aggregationSection}</div>
            {searchSection}
            {tableBlock}
            {paginationBlock}

            <TaskModal
              visible={isModalVisible}
              onClose={() => {
                setIsModalVisible(false);
                setSelectedTask(undefined);
                // Keep taskId in URL so the filter stays applied when modal is closed
              }}
              task={selectedTask}
              onTaskSaved={handleTaskSaved}
              foremanAreas={foremanAreas}
              taskType={selectedTask?.type || 'Overhead'}
              initialContractAreas={[]}
            />

            {selectedTaskForCompletion && (
              <CompletionModal
                visible={isCompletionModalVisible}
                onClose={() => {
                  setIsCompletionModalVisible(false);
                  setSelectedTaskForCompletion(null);
                }}
                task={selectedTaskForCompletion}
                onComplete={handleTaskComplete}
              />
            )}

            {selectedTaskForHistory && (
              <TaskHistoryModal
                visible={isHistoryModalVisible}
                onClose={() => {
                  setIsHistoryModalVisible(false);
                  setSelectedTaskForHistory(null);
                }}
                task={selectedTaskForHistory}
                onAddComment={handleAddHistoryComment}
              />
            )}

            <PrintTaskPDFModal
              task={printPdfTask}
              onClose={() => setPrintPdfTask(null)}
            />

            {selectedTaskForAction && (
              <>
                <RequestCorrectionModal
                  visible={isRequestCorrectionModalVisible}
                  onClose={() => {
                    setIsRequestCorrectionModalVisible(false);
                    setSelectedTaskForAction(null);
                  }}
                  task={selectedTaskForAction}
                  onSubmit={handleRequestCorrection}
                />

                <CloseTaskModal
                  visible={isCloseTaskModalVisible}
                  onClose={() => {
                    setIsCloseTaskModalVisible(false);
                    setSelectedTaskForAction(null);
                  }}
                  task={selectedTaskForAction}
                  onSubmit={(comments) => handleCloseTask(selectedTaskForAction.id, comments.toString())}
                />

                <ReopenTaskModal
                  visible={isReopenTaskModalVisible}
                  onClose={() => {
                    setIsReopenTaskModalVisible(false);
                    setSelectedTaskForAction(null);
                  }}
                  task={selectedTaskForAction}
                  onSubmit={(comments) => handleReopenTask(comments)}
                />

                <SetInternalReviewModal
                  visible={isSetInternalReviewModalVisible}
                  onClose={() => setIsSetInternalReviewModalVisible(false)}
                  task={selectedTaskForAction}
                  onSubmit={handleSetInternalReview}
                />
              </>
            )}

            <ResponsiveModal
              isOpen={isConvertToRoutineModalVisible}
              onClose={() => {
                setIsConvertToRoutineModalVisible(false);
                setSelectedTaskForConvertToRoutine(null);
              }}
              title="Convert to Routine"
              footer={
                <>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsConvertToRoutineModalVisible(false);
                      setSelectedTaskForConvertToRoutine(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirmConvertToRoutine}
                  >
                    Confirm
                  </Button>
                </>
              }
            >
              <p className="tw-mb-0">
                Should this task be moved to Routine? You can then complete routine details (allocations, etc.) and save.
              </p>
              {selectedTaskForConvertToRoutine && (
                <p className="tw-mt-2 tw-mb-0 tw-text-content-muted tw-text-sm">
                  Task: {selectedTaskForConvertToRoutine.taskNumber || selectedTaskForConvertToRoutine.id}
                  {selectedTaskForConvertToRoutine.description ? ` — ${selectedTaskForConvertToRoutine.description}` : ''}
                </p>
              )}
            </ResponsiveModal>

            <RoutineTaskModal
              visible={showRoutineTaskModal}
              onClose={() => {
                setShowRoutineTaskModal(false);
                setSelectedRoutineTask(null);
              }}
              task={selectedRoutineTask || undefined}
              project={
                selectedRoutineTask?.projectId
                  ? projects.find(p => p.id === selectedRoutineTask!.projectId)
                  : undefined
              }
              // When opening from Quantified → Routine conversion, do not auto-populate
              // the Base Task; let the user explicitly select it.
              autoPopulateBaseTask={false}
              onTaskSaved={async () => {
                const isNew = !selectedRoutineTask;
                setShowRoutineTaskModal(false);
                setSelectedRoutineTask(null);
                if (isNew) {
                  refreshListAfterNewTask();
                } else {
                  await fetchTasks();
                }
                showToast('Routine task saved', 'success');
              }}
            />

            <CancelTaskModal
              visible={isCancelTaskModalVisible}
              onClose={() => {
                setIsCancelTaskModalVisible(false);
                setSelectedTasksForCancel([]);
              }}
              tasks={selectedTasksForCancel}
              onSubmit={handleCancelTask}
            />

            <CloneTaskConfirmModal
              show={cloneConfirmTasks !== null && cloneConfirmTasks.length > 0}
              onHide={() => setCloneConfirmTasks(null)}
              onConfirm={(names, quantifiedOptions) => {
                const toClone = cloneConfirmTasks;
                setCloneConfirmTasks(null);
                if (toClone?.length) {
                  void handleDuplicateTasks(toClone, names, quantifiedOptions);
                }
              }}
              items={(cloneConfirmTasks ?? []).map((t) => ({
                taskId: t.id,
                sourceTaskLabel: t.taskNumber || `#${t.id}`,
                sourceTaskName: t.name,
                taskType: t.type,
                completionAccomplishment: t.completionAccomplishment ?? 0,
                contractYear: t.contractYear,
                serviceAreaId:
                  t.foremanArea?.serviceAreaId ??
                  t.project?.serviceAreaId ??
                  null
              }))}
            />

            <ImportModal
              visible={isImportModalVisible}
              onClose={() => setIsImportModalVisible(false)}
              onImport={handleImportTasks}
            />

            <UnapprovedSubmissionsWarning
              isVisible={showUnapprovedWarning}
              submissionCount={unapprovedSubmissionCount}
              submissions={unapprovedSubmissions}
              onConfirm={handleUnapprovedWarningConfirm}
              onCancel={handleUnapprovedWarningCancel}
              actionType={pendingAction || 'complete'}
            />
    </div>
  );
};

export default QPTracking; 